import { useParams } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from '@kostbar/i18n'
import { AlertCircle, Save } from 'lucide-react'
import {
  useRestaurantScheduling,
  useSaveRestaurantScheduling,
} from '../hooks/use-restaurant-scheduling'
import { HoursForm } from '../components/hours-form'
import { SlotsForm } from '../components/slots-form'
import { LeadWindowsEditor } from '../components/lead-windows-editor'
import { OrderingForm } from '../components/ordering-form'
import { ExceptionsEditor } from '../components/exceptions-editor'
import { SchedulingPreviewPanel } from '../components/scheduling-preview-panel'
import { defaultScheduling, nestingWarnings } from '../lib/scheduling'
import type { RestaurantScheduling } from '@/api/types'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'

export function RestaurantSchedulingPage() {
  const { t: tRestaurants } = useTranslation('restaurants')
  const { id } = useParams({ strict: false })
  const restaurantId = Number(id)
  const { data, isLoading } = useRestaurantScheduling(restaurantId)
  const save = useSaveRestaurantScheduling(restaurantId)

  const [scheduling, setScheduling] =
    useState<RestaurantScheduling>(defaultScheduling())

  useEffect(() => {
    if (data) setScheduling(data)
  }, [data])

  const patch = (next: Partial<RestaurantScheduling>) =>
    setScheduling((prev) => ({ ...prev, ...next }))

  const warnings = useMemo(() => nestingWarnings(scheduling), [scheduling])
  const hasWarnings =
    warnings.pickupOutsideStaffed.length > 0 ||
    warnings.staffedOutsideUnmanned.length > 0

  const dayLabels = (days: Array<string>) =>
    days
      .map((d) => tRestaurants(`scheduling.hours.day.${d}` as const))
      .join(', ')

  return (
    <PermissionGate action={ACTIONS.RESTAURANT_SCHEDULING_MANAGE}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">
              {tRestaurants('scheduling.title')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {tRestaurants('scheduling.subtitle')}
            </p>
          </div>
          <Button
            onClick={() => save.mutate(scheduling)}
            disabled={save.isPending || isLoading}
          >
            <Save className="mr-2 h-4 w-4" />
            {save.isPending
              ? tRestaurants('scheduling.saving')
              : tRestaurants('scheduling.save')}
          </Button>
        </div>

        {hasWarnings && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{tRestaurants('scheduling.nesting.title')}</AlertTitle>
            <AlertDescription>
              <p>{tRestaurants('scheduling.nesting.hint')}</p>
              <ul className="mt-1 list-inside list-disc">
                {warnings.staffedOutsideUnmanned.length > 0 && (
                  <li>
                    {tRestaurants('scheduling.nesting.staffedOutsideUnmanned', {
                      days: dayLabels(warnings.staffedOutsideUnmanned),
                    })}
                  </li>
                )}
                {warnings.pickupOutsideStaffed.length > 0 && (
                  <li>
                    {tRestaurants('scheduling.nesting.pickupOutsideStaffed', {
                      days: dayLabels(warnings.pickupOutsideStaffed),
                    })}
                  </li>
                )}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="hours" className="w-full">
          <TabsList>
            <TabsTrigger value="hours">
              {tRestaurants('scheduling.tabs.hours')}
            </TabsTrigger>
            <TabsTrigger value="ordering">
              {tRestaurants('scheduling.tabs.ordering')}
            </TabsTrigger>
            <TabsTrigger value="exceptions">
              {tRestaurants('scheduling.tabs.exceptions')}
            </TabsTrigger>
            <TabsTrigger value="preview">
              {tRestaurants('scheduling.tabs.preview')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="hours" className="mt-6 space-y-6">
            <HoursForm
              title={tRestaurants('scheduling.hours.unmannedTitle')}
              hint={tRestaurants('scheduling.hours.unmannedHint')}
              value={scheduling.unmannedHours}
              onChange={(v) => patch({ unmannedHours: v })}
            />
            <HoursForm
              title={tRestaurants('scheduling.hours.staffedTitle')}
              hint={tRestaurants('scheduling.hours.staffedHint')}
              value={scheduling.staffedHours}
              onChange={(v) => patch({ staffedHours: v })}
            />
          </TabsContent>

          <TabsContent value="ordering" className="mt-6 space-y-6">
            <OrderingForm
              value={scheduling.ordering}
              onChange={(v) => patch({ ordering: v })}
            />
            <Card>
              <CardHeader>
                <CardTitle>{tRestaurants('scheduling.slots.title')}</CardTitle>
                <CardDescription>
                  {tRestaurants('scheduling.slots.hint')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SlotsForm
                  value={scheduling.slots}
                  onChange={(v) => patch({ slots: v })}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>{tRestaurants('scheduling.lead.title')}</CardTitle>
                <CardDescription>
                  {tRestaurants('scheduling.lead.hint')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <LeadWindowsEditor
                  value={scheduling.leadWindows}
                  onChange={(v) => patch({ leadWindows: v })}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="exceptions" className="mt-6">
            <ExceptionsEditor
              value={scheduling.exceptions}
              onChange={(v) => patch({ exceptions: v })}
            />
          </TabsContent>

          <TabsContent value="preview" className="mt-6">
            <SchedulingPreviewPanel
              restaurantId={restaurantId}
              scheduling={scheduling}
            />
          </TabsContent>
        </Tabs>
      </div>
    </PermissionGate>
  )
}
